import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import { AdminUser, fetchAdminUsers, reviewUserVerification, VerificationStatus } from '../api/users';
import { downloadVerificationDocument, listVerificationDocuments } from '../api/kyc';
import { PageHeader } from '../components/ui/PageHeader';
import { Spinner } from '../components/ui/Spinner';
import { formatDateTime } from '../lib/format';

const STATUS_FILTERS: Array<{ key: VerificationStatus | 'all'; label: string }> = [
  { key: 'pending', label: 'En attente' },
  { key: 'verified', label: 'Validés' },
  { key: 'rejected', label: 'Rejetés' },
  { key: 'all', label: 'Tous' }
];

const STATUS_BADGE: Record<VerificationStatus, string> = {
  pending: 'bg-amber-100 text-amber-800',
  verified: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700'
};

const STATUS_LABEL: Record<VerificationStatus, string> = {
  pending: 'En attente',
  verified: 'Vérifié',
  rejected: 'Rejeté'
};

export const AdminKycReviewPage = () => {
  const [filter, setFilter] = useState<VerificationStatus | 'all'>('pending');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users-kyc', filter],
    queryFn: () =>
      fetchAdminUsers(filter === 'all' ? { limit: 50 } : { limit: 50, verificationStatus: filter })
  });

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="Vérification KYC"
        description="Examinez les documents soumis par les utilisateurs et statuez sur leur vérification d'identité."
      />

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
              filter === f.key
                ? 'border-brand-500 bg-brand-500 text-white shadow-md'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            }`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {usersQuery.isLoading && (
        <div className="card flex flex-col items-center justify-center gap-3 p-10 text-slate-500">
          <Spinner />
          <p className="text-sm">Chargement des utilisateurs…</p>
        </div>
      )}

      {usersQuery.error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {usersQuery.error instanceof ApiError ? usersQuery.error.message : 'Erreur de chargement'}
        </div>
      )}

      {usersQuery.data && usersQuery.data.data.length === 0 && (
        <div className="card p-10 text-center">
          <p className="text-sm text-slate-500">
            Aucun utilisateur avec le statut « {filter === 'all' ? 'tous' : STATUS_LABEL[filter as VerificationStatus]} ».
          </p>
        </div>
      )}

      {usersQuery.data && usersQuery.data.data.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.4fr]">
          <div className="card overflow-hidden">
            <div className="border-b border-slate-100 bg-slate-50/60 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {usersQuery.data.meta.total} utilisateur{usersQuery.data.meta.total !== 1 ? 's' : ''}
            </div>
            <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {usersQuery.data.data.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedUser(u)}
                    className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${
                      selectedUser?.id === u.id ? 'bg-brand-50/60' : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-900">
                        {u.firstName} {u.lastName}
                      </p>
                      <p className="truncate text-xs text-slate-500">{u.email}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">
                        {u.role}
                        {u.verificationSubmissionDate && (
                          <span className="ml-2 text-slate-500">
                            Soumis : {formatDateTime(u.verificationSubmissionDate)}
                          </span>
                        )}
                      </p>
                    </div>
                    <span className={`badge ${STATUS_BADGE[u.verificationStatus]}`}>
                      {STATUS_LABEL[u.verificationStatus]}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            {selectedUser ? (
              <UserReviewPanel
                user={selectedUser}
                onReviewed={() => {
                  usersQuery.refetch();
                  setSelectedUser(null);
                }}
              />
            ) : (
              <div className="card flex h-full min-h-[300px] items-center justify-center p-10 text-center text-sm text-slate-500">
                Sélectionnez un utilisateur dans la liste pour examiner ses documents.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UserReviewPanel = ({ user, onReviewed }: { user: AdminUser; onReviewed: () => void }) => {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');

  const docsQuery = useQuery({
    queryKey: ['kyc-docs', user.id],
    queryFn: () => listVerificationDocuments(user.id)
  });

  const review = useMutation({
    mutationFn: (decision: 'verified' | 'rejected') =>
      reviewUserVerification(user.id, { status: decision, notes: notes.trim() || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users-kyc'] });
      onReviewed();
    }
  });

  return (
    <div className="card p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {user.firstName} {user.lastName}
          </h2>
          <p className="text-sm text-slate-500">{user.email}</p>
          <p className="mt-1 text-xs text-slate-400">
            Rôle : <span className="font-medium text-slate-600">{user.role}</span>
            {user.phoneNumber && <span className="ml-2">· {user.phoneNumber}</span>}
          </p>
        </div>
        <span className={`badge ${STATUS_BADGE[user.verificationStatus]}`}>
          {STATUS_LABEL[user.verificationStatus]}
        </span>
      </div>

      <div className="mt-6">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Documents soumis</h3>
        {docsQuery.isLoading && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <Spinner className="h-4 w-4 border-2 border-slate-300 border-t-brand-500" />
            Chargement…
          </div>
        )}
        {docsQuery.error && (
          <p className="mt-3 text-sm text-red-600">
            {docsQuery.error instanceof ApiError ? docsQuery.error.message : 'Erreur de chargement des documents'}
          </p>
        )}
        {docsQuery.data && docsQuery.data.data.length === 0 && (
          <p className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/40 px-3 py-3 text-sm text-slate-500">
            Aucun document soumis pour cet utilisateur.
          </p>
        )}
        {docsQuery.data && docsQuery.data.data.length > 0 && (
          <ul className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200">
            {docsQuery.data.data.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-800">{doc.originalName}</p>
                  <p className="text-xs text-slate-500">
                    {doc.documentType} · {(doc.size / 1024).toFixed(1)} Ko · {formatDateTime(doc.createdAt)}
                  </p>
                </div>
                <button
                  type="button"
                  className="text-xs font-medium text-brand-600 hover:underline"
                  onClick={async () => {
                    try {
                      const { blob, filename } = await downloadVerificationDocument(user.id, doc.id);
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = filename;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      alert((e as Error).message);
                    }
                  }}
                >
                  Télécharger
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6">
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Notes (optionnel)
          </span>
          <textarea
            className="input mt-1.5"
            rows={3}
            placeholder="Justificatif accepté, motif de rejet, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
      </div>

      {review.error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {review.error instanceof ApiError ? review.error.message : 'Erreur lors de la décision'}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          className="btn-primary"
          disabled={review.isPending}
          onClick={() => review.mutate('verified')}
        >
          {review.isPending && review.variables === 'verified' ? (
            <>
              <Spinner className="h-4 w-4 border-2 border-white border-t-transparent" />
              Validation…
            </>
          ) : (
            '✓ Valider la vérification'
          )}
        </button>
        <button
          type="button"
          className="btn-ghost border-red-200 text-red-700 hover:bg-red-50"
          disabled={review.isPending}
          onClick={() => review.mutate('rejected')}
        >
          {review.isPending && review.variables === 'rejected' ? (
            <>
              <Spinner className="h-4 w-4 border-2 border-red-400 border-t-transparent" />
              Rejet…
            </>
          ) : (
            '✗ Rejeter'
          )}
        </button>
      </div>
    </div>
  );
};
